/**
 * Every sound Block Jam can play (WP-37.5, Module 37 §3).
 *
 * 18 SFX + 1 music track. The ids are namespaced with `blockjam_` because
 * `HowlerAdapter` resolves an id straight to `/audio/{id}.mp3` and both games
 * share that directory — and because the composition root merges the two games'
 * cooldown tables into one map, where an unprefixed `button_tap` would collide.
 *
 * Registering an id here is only half the job: `blockjam-manifest.ts` derives
 * its `audioManifest` from this enum, and that manifest is what decides the
 * playback CHANNEL (WP-37.2) — i.e. which volume slider the sound obeys.
 */
export var BlockJamAudioId;
(function (BlockJamAudioId) {
    // ─── Gameplay ──────────────────────────────────────────────────────────────
    /**
     * The selection CHANGED to a block — nothing was selected, or something else
     * was. Not "the pointer went down": pressing the block already in hand is
     * `BLOCK_RESELECT`, and a press is the only way either of them happens.
     */
    BlockJamAudioId["BLOCK_SELECT"] = "blockjam_block_select";
    /**
     * A press landed on the block that is already selected (WP-D.1). Deliberately
     * quieter and duller than `BLOCK_SELECT`: it acknowledges the press without
     * claiming a selection that did not happen.
     */
    BlockJamAudioId["BLOCK_RESELECT"] = "blockjam_block_reselect";
    /**
     * The mouse entered a block. Desktop only — the board refuses `touch`/`pen`
     * pointers, because a finger has no hover state (WP-D.1).
     */
    BlockJamAudioId["BLOCK_HOVER"] = "blockjam_block_hover";
    /**
     * The selection genuinely went to nothing — today, a tap on empty board while
     * a block was selected. **Not** a release: the block stays selected when the
     * pointer comes up, which is why sounding this there was Bug 4.
     */
    BlockJamAudioId["BLOCK_DESELECT"] = "blockjam_block_deselect";
    /** The settle, when a dragged block lands on its new cells. Not the drag itself. */
    BlockJamAudioId["BLOCK_MOVE"] = "blockjam_block_move";
    /** A move that cannot happen — shoved into a wall, or into another block. */
    BlockJamAudioId["INVALID_MOVE"] = "blockjam_invalid_move";
    /**
     * A block leaving the board through a matching gate. Fires on the gate
     * FLASH, not on the move commit (§5, "no sound fires ahead of its
     * animation") — wired in WP-37.8.
     */
    BlockJamAudioId["BLOCK_EXIT"] = "blockjam_block_exit";
    // ─── Session ───────────────────────────────────────────────────────────────
    BlockJamAudioId["UNDO"] = "blockjam_undo";
    BlockJamAudioId["RESTART"] = "blockjam_restart";
    BlockJamAudioId["LEVEL_START"] = "blockjam_level_start";
    /** Rides the confetti burst, not the win detection. */
    BlockJamAudioId["LEVEL_COMPLETE"] = "blockjam_level_complete";
    /**
     * No legal moves remain (`StuckPopup`). A soft downward cue, explicitly NOT
     * a failure sting — Block Jam has no fail state and the player recovers with
     * undo or restart (locked decision 9).
     */
    BlockJamAudioId["STUCK"] = "blockjam_stuck";
    // ─── UI ────────────────────────────────────────────────────────────────────
    /**
     * Hooked at Block Jam's OWN ui layer only, never in `packages/ui` (locked
     * decision 10) — Water Sort must not silently inherit cues nobody approved
     * for it.
     */
    BlockJamAudioId["BUTTON_TAP"] = "blockjam_button_tap";
    BlockJamAudioId["POPUP_OPEN"] = "blockjam_popup_open";
    BlockJamAudioId["POPUP_CLOSE"] = "blockjam_popup_close";
    // ─── Power-ups ─────────────────────────────────────────────────────────────
    /** Selecting a power-up and entering targeting — before anything is spent. */
    BlockJamAudioId["POWERUP_ARMED"] = "blockjam_powerup_armed";
    /**
     * The three impact cues. WP-35.19 gave all three power-ups ONE shared
     * `POWERUP_SMASH` as an asset-economy measure; locked decision 8 reverses
     * that, so each is now audibly distinct. Which id an effect uses is declared
     * as data on the effect itself (`PowerupEffect.impactAudioId`) — the plugin
     * stays id-agnostic, exactly as it does for `fx` and `activation`.
     */
    BlockJamAudioId["POWERUP_HAMMER"] = "blockjam_powerup_hammer";
    BlockJamAudioId["POWERUP_RAINBOW"] = "blockjam_powerup_rainbow";
    BlockJamAudioId["POWERUP_COLORDELETE"] = "blockjam_powerup_colordelete";
    /** Module 63 (WP-63.E4) — Surge's activation beat: the shut gates opening. */
    BlockJamAudioId["POWERUP_SURGE"] = "blockjam_powerup_surge";
    /** Module 67 (WP-67.B4) — Gate Widener's impact beat: the gate growing a cell. */
    BlockJamAudioId["POWERUP_WIDENER"] = "blockjam_powerup_widener";
    // ─── Powered gates (Module 63, WP-63.C4) ───────────────────────────────────
    /**
     * The four beats of a powered circuit. All four ride an ANIMATION, never an
     * input (Audio_Rules Rule 1.4): `GATE_OPEN`/`GATE_CLOSE` fire from the paint
     * that starts the trace lighting/draining — during a drag that is the frame
     * a whole-cell step lands on or leaves the plate, not the release —
     * `GATE_BLOCKED` from the shutter's shudder, and `TRACE_PULSE` from the
     * pulse's own first frame, `shutterMs` later, so the refusal is two beats
     * (clunk, then the charge running toward the gate), not one noise.
     *
     * `GATE_BLOCKED` REPLACES `INVALID_MOVE` for that shove: a wall, a colour
     * mismatch or an axis lock still buzz; only a shut powered gate clunks.
     */
    BlockJamAudioId["GATE_OPEN"] = "blockjam_gate_open";
    BlockJamAudioId["GATE_CLOSE"] = "blockjam_gate_close";
    BlockJamAudioId["GATE_BLOCKED"] = "blockjam_gate_blocked";
    BlockJamAudioId["TRACE_PULSE"] = "blockjam_trace_pulse";
    // ─── Moves running out (Module 103, WP-103.3) ─────────────────────────────
    /**
     * The 3·2·1 warning: a rising wood tick, one id per step so each can be
     * auditioned, swapped or excluded on its own (Audio_Rules Rule 9). It rides
     * the STATE TRANSITION "moves left became 3 / 2 / 1" (Rule 1b), decided once
     * in `_afterMove` after a committed move that neither won, stuck nor ran out —
     * never on Undo (which spends nothing back), Restart or an ad grant, none of
     * which commit a move. Its frame is the dial's slam, so it is warmed at boot.
     */
    BlockJamAudioId["MOVES_LOW_3"] = "blockjam_moves_low_3";
    BlockJamAudioId["MOVES_LOW_2"] = "blockjam_moves_low_2";
    BlockJamAudioId["MOVES_LOW_1"] = "blockjam_moves_low_1";
    // ─── Coins (Module 110, WP-110.4) ─────────────────────────────────────────
    /**
     * A coin of a REWARD reaching the Top Bar's coin: one per landing of the
     * trail (3–4 per reward), on the landing frame. Rides the coin animation, not
     * the credit (Rule 1b: the state transition is "a coin arrived", and the
     * credit itself happens out of sight). Without a flight (reduced motion, an
     * unmeasurable end) it sounds once, when the pill counts.
     */
    BlockJamAudioId["COIN_LAND"] = "blockjam_coin_land";
    /**
     * A SPEND leaving the Top Bar for the button it paid (a power-up, SOLVE IT):
     * once per spend, as the first coin leaves. Quieter and lower than the
     * landing: a spend is the player's own act, confirmed, not celebrated.
     */
    BlockJamAudioId["COIN_SPEND"] = "blockjam_coin_spend";
    // ─── Free uses (Module 113, WP-113.2) ─────────────────────────────────────
    /**
     * A FREE power-up use's ticket reaching its button: once per free use, on the
     * landing frame of the ticket's hop (Rule 1b: the transition is "the ticket
     * went in", the free use itself was spent out of sight). Light and papery,
     * never a coin sound — a coin would say coins were spent. Without a flight
     * (reduced motion) it sounds once, when the free use is spent.
     */
    BlockJamAudioId["FREE_TICKET"] = "blockjam_free_ticket";
    // ─── Milestones (Module 132, WP-132.6) ────────────────────────────────────
    /**
     * A milestone level claimed (the Milestones popup's Claim, or home's peek):
     * once per claim, on the tap, with the medal's stamp (mockup G1). The coins'
     * own landing ticks follow. Excluded until auditioned (Module 56): the button
     * tap still answers the tap meanwhile.
     */
    BlockJamAudioId["MILESTONE_CLAIM"] = "blockjam_milestone_claim";
    /**
     * WP-132.7 — the full-screen Gold / Platinum reveal: once, on the dropping
     * medal's landing frame (Rule 1b), or at once on the still reduced-motion
     * card. Excluded until auditioned (Module 56).
     */
    BlockJamAudioId["MILESTONE_REVEAL"] = "blockjam_milestone_reveal";
    // ─── Music ─────────────────────────────────────────────────────────────────
    /**
     * The `music` channel, and therefore the only thing the `musicVolume` slider
     * has ever controlled. Block Jam only — the lobby and Water Sort stay silent
     * (locked decision 6).
     *
     * Module 64 turned the single id into a POOL. Four: the roadmap promised Playgama
     * "three further tracks", and WP-64.B3 loop-wrapped the two that could not
     * loop before. Every one is trimmed to the same bed level, measured, not
     * requested — see `CREDITS.md`. The rotation picks one of these
     * per level band; which one is not decided here, and no call site names a
     * specific track — see `createLevelMusicService`.
     *
     * All four MUST appear in the manifest's `AUDIO_TRACKS` (WP-37.2): that list
     * is what the derived channel map reads, and an id missing from it resolves to
     * `sfx`, where it would ignore the music slider entirely and play at cue
     * volume. `blockjam-manifest.test.ts` pins it, with a negative control.
     *
     * The `_1` suffix on the first is deliberate: it was `blockjam_bg_music` until
     * Module 64, and the rename buys symmetry at the cost of one orphaned file
     * that `materializeAll()` deletes by itself (Module 56 made it authoritative
     * over `public/audio/*.mp3`, and `--check` fails the build if it does not).
     */
    BlockJamAudioId["BG_MUSIC_1"] = "blockjam_bg_music_1";
    BlockJamAudioId["BG_MUSIC_2"] = "blockjam_bg_music_2";
    BlockJamAudioId["BG_MUSIC_3"] = "blockjam_bg_music_3";
    BlockJamAudioId["BG_MUSIC_4"] = "blockjam_bg_music_4";
    /** Module 72 — the rotation grew to five tracks from the listening page. */
    BlockJamAudioId["BG_MUSIC_5"] = "blockjam_bg_music_5";
    /**
     * Module 72 (WP-72.B3) — the Blockway home screen's own track. A music-channel
     * id, but NOT in the per-puzzle rotation (`BLOCKJAM_MUSIC_ROTATION`).
     */
    BlockJamAudioId["HOME_MUSIC"] = "blockjam_home_music";
})(BlockJamAudioId || (BlockJamAudioId = {}));
/**
 * Minimum gap between repeats of the same cue, in milliseconds (WP-37.3).
 *
 * Lives here, beside the ids it tunes, rather than in `packages/audio`: a spam
 * threshold is a per-game judgement, and putting it in the shared package meant
 * every tweak touched a package both games depend on. The composition root
 * merges this with the other games' tables when it builds the `AudioService`.
 *
 * An id absent from this table has NO cooldown, which is the right default for
 * anything the player triggers deliberately and one at a time — every session
 * cue, both loops, and all four power-up cues.
 */
export const BLOCKJAM_AUDIO_COOLDOWNS = {
    /**
     * Tactile enough to feel instant, but a fast tap-storm over the board must
     * not machine-gun. Select and deselect share a value because they alternate.
     */
    [BlockJamAudioId.BLOCK_SELECT]: 60,
    [BlockJamAudioId.BLOCK_DESELECT]: 60,
    /** Same beat as select — the two are alternatives to the same press. */
    [BlockJamAudioId.BLOCK_RESELECT]: 60,
    /**
     * The one cue the player does not ASK for: it fires from moving the mouse.
     * The board already limits it to one per block entered, so this only guards
     * the sweep — dragging the cursor fast across a packed board crosses blocks
     * far quicker than a tap-storm ever produces presses.
     */
    [BlockJamAudioId.BLOCK_HOVER]: 90,
    /** Tapping the same action button repeatedly should not machine-gun. */
    [BlockJamAudioId.BUTTON_TAP]: 50,
    /** A popup cannot legitimately open or close twice within a tenth of a second. */
    [BlockJamAudioId.POPUP_OPEN]: 100,
    [BlockJamAudioId.POPUP_CLOSE]: 100,
    /**
     * Deliberately the FASTEST to retrigger. WP-36.4.1 made concurrent exits
     * possible — several blocks can clear their gates in the same beat, and each
     * one has to be audible, so this gap only guards against a literal
     * double-fire of the same frame.
     */
    [BlockJamAudioId.BLOCK_EXIT]: 40,
    /**
     * Deliberately the SLOWEST to retrigger. Shoving a blocked piece at a wall is
     * the single spammiest input in the game — the player holds and pushes, and
     * every rejected step would otherwise fire the buzz again.
     */
    [BlockJamAudioId.INVALID_MOVE]: 220,
    /**
     * Module 63 — a drag jittering across a plate's edge flips the circuit on
     * every step; the paint follows each flip (that is the live preview), the
     * SOUND must not. Long enough to swallow the jitter, short enough that a
     * deliberate off-and-back-on still sounds twice.
     */
    [BlockJamAudioId.GATE_OPEN]: 120,
    [BlockJamAudioId.GATE_CLOSE]: 120,
    /**
     * The board already fires these once per shove (`blockedNotified`); the
     * cooldown only guards the same-frame double. Capped at `INVALID_MOVE`'s
     * gap, which is the table's ceiling by test.
     */
    [BlockJamAudioId.GATE_BLOCKED]: 220,
    [BlockJamAudioId.TRACE_PULSE]: 220,
    /**
     * Module 110 — landings are 110 ms apart, so this only guards a same-frame
     * double; it must stay well under the trail's gap or it swallows coins. Equal
     * to `block_exit`'s, which is the table's floor by test.
     */
    [BlockJamAudioId.COIN_LAND]: 40,
};
/**
 * Module 50 (WP-50.11 — AUD-023) — the cues that are warmed at boot.
 *
 * `HowlerAdapter` builds each `Howl` on the id's FIRST play, which
 * `Audio_Rules.md` states as the design. The audit measured the consequence:
 * 24–83ms between `play()` and the sound for most cues — inside the window where
 * a sound still reads as attached to the press that caused it — and **175ms for
 * `level_complete`**, which exists to ride the confetti burst. On the very first
 * level a player clears, it lands a sixth of a second after the burst it is part
 * of. Every later win is right, because the file is cached by then.
 *
 * So the rule, now written into `Audio_Rules.md`: **a cue that rides an
 * animation is warmed; a cue that answers a press is lazy.** These two are the
 * whole of the first list — `level_complete` (12KB) rides the confetti and
 * `block_exit` (WP-36.5) rides the slide-out.
 *
 * Deliberately not all 21. That is 336KB of fetches during the loading screen,
 * which trades a compliance metric CrazyGames actually measures (time to
 * playable) for a cosmetic one. `bg_music_*` are the largest single files and the
 * worst measured delay (222ms) and are still NOT here: it is a bed that fades in
 * under an already-running level, not a cue pinned to a frame.
 */
export const BLOCKJAM_ANIMATION_CUES = [
    BlockJamAudioId.LEVEL_COMPLETE,
    BlockJamAudioId.BLOCK_EXIT,
    // Module 63 (WP-63.C4) — all four ride a frame they can be late for. The
    // first powered gate a player meets is at L25, well after boot, so the
    // warm-up costs nothing the player waits on.
    BlockJamAudioId.GATE_OPEN,
    BlockJamAudioId.GATE_CLOSE,
    BlockJamAudioId.GATE_BLOCKED,
    BlockJamAudioId.TRACE_PULSE,
    // Module 103 (WP-103.3) — each rides the dial's slam.
    BlockJamAudioId.MOVES_LOW_3,
    BlockJamAudioId.MOVES_LOW_2,
    BlockJamAudioId.MOVES_LOW_1,
    // Module 110 (WP-110.4) — each rides a coin's frame.
    BlockJamAudioId.COIN_LAND,
    BlockJamAudioId.COIN_SPEND,
    // Module 113 (WP-113.2) — rides the ticket's landing frame.
    BlockJamAudioId.FREE_TICKET,
    // Module 139 (D7) — rides the frame the hammer's head lands on.
    BlockJamAudioId.POWERUP_HAMMER,
    // Module 140 (D4) — rides the frame the first Master Key turns in its lock.
    BlockJamAudioId.POWERUP_SURGE,
    // Module 140 (D9) — rides the frame the Colour Bomb lands on.
    BlockJamAudioId.POWERUP_COLORDELETE,
];
